import React, { Fragment, useEffect, useMemo } from 'react'
import { Habitacion, ModalidadDePago, ModalidadGrupoTarifa, TiposAlojamientos } from 'src/gql/schema'
import Detail from 'src/shared/components/data-display/Detail/Detail'
import DetailContent from 'src/shared/components/data-display/Detail/kit/DetailContent/DetailContent'
import DetailDivider from 'src/shared/components/data-display/Detail/kit/DetailDivider/DetailDivider'
import DetailIcon from 'src/shared/components/data-display/Detail/kit/DetailIcon/DetailIcon'
import DetailRow from 'src/shared/components/data-display/Detail/kit/DetailRow/DetailRow'
import DetailText from 'src/shared/components/data-display/Detail/kit/DetailText/DetailText'
import Resumen from 'src/shared/components/data-display/Resumen/Resumen'
import ResumenContent from 'src/shared/components/data-display/Resumen/ResumenContent/ResumenContent'
import ResumenFooter from 'src/shared/components/data-display/Resumen/ResumenFooter/ResumenFooter'
import ResumenTitle from 'src/shared/components/data-display/Resumen/ResumenTitle/ResumenTitle'
import { useCosts } from '../../context/CostContext'
import { capitalize } from 'src/shared/helpers/capitalize'
import { sum, times } from 'src/shared/helpers/calculator'
import { useFormContext } from 'react-hook-form'
import { ExtensionEstanciaValues } from '../../EstensionEstancia-types'
import ResumenSection from 'src/shared/components/data-display/Resumen/ResumenSection/ResumenSection'
import { formatCurrencyToFixed } from 'src/shared/hooks/formatCurrency'
import { useIVA } from 'src/shared/hooks/useIVA'
import { formatDateShort } from '../Principal/sections/InputTarifaBase/formatDateShort'
import { useDate } from 'src/shared/hooks/useDate'
import { useFormatDate } from 'src/shared/hooks/useFormatDate'
import { formatTipoPago } from 'src/shared/helpers/formatTipoPago'
import DetailAction from 'src/shared/components/data-display/Detail/kit/DetailAction/DetailAction'
import { getCurrencyFormat } from 'src/utils/string'
import { addDays } from 'src/shared/helpers/addDays'
import { addExtraHours } from 'src/shared/helpers/addExtraHours'

const RenovacionResumen = ({ formId, habitacion, onOpenDescuento, descuento, descuento_unico, descuentoDesactivado }: {
    formId: string, habitacion?: Habitacion, onOpenDescuento: () => void, descuento: {
        total: number;
        nombre: string;
        tipo_descuento?: string;
        valor?: number;
    },
    descuento_unico: boolean
    descuentoDesactivado: boolean
}) => {

    const {
        gruposTarifasSelected,
        gruposTarifasAdicionalesSelected,
        setCosts,
        costs,
        tarifaBaseHabitacion
    } = useCosts()

    const { UTCStringToLocalDate, diffDays, areSameDay } = useDate()
    const { setStartDate, formatCustomDate } = useFormatDate()
    const { getIVA, getSubtotal } = useIVA()

    const {
        watch,
        getValues
    } = useFormContext<ExtensionEstanciaValues>()

    const hospedajesExtra = watch("principal.nochesExtraCount")
    const horasExtras = watch("principal.horasExtraCount")
    const personasExtras = watch("personas.extraCount")
    const pagos = watch("pago.items")
    const tipoPago = watch("pago.type")

    const isMotel = habitacion?.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Motel

    const totalPropinas = sum(pagos?.map(p => {
        if (!p?.propina) return 0
        const porcentaje = p.propina.porcentaje
        if (porcentaje && porcentaje !== "Otro") return times(costs.totalConIva, Number(porcentaje))
        return Number(p.propina.monto) || 0
    }) || [0])

    // Grupos ordenados por jerarquía: adicional (0) > temporada (1) > base (2)
    const priorityGroups = useMemo(() => [
        ...gruposTarifasAdicionalesSelected.map(gp => ({ gp, priority: 0 })),
        ...gruposTarifasSelected
            .filter(gp => gp.modalidad !== ModalidadGrupoTarifa.Base)
            .map(gp => ({ gp, priority: 1 })),
        ...gruposTarifasSelected
            .filter(gp => gp.modalidad === ModalidadGrupoTarifa.Base)
            .map(gp => ({ gp, priority: 2 })),
    ], [gruposTarifasSelected, gruposTarifasAdicionalesSelected])

    // Noche por noche: determina qué tarifa aplica respetando jerarquía
    const { effectiveNights, tarifaDateRanges, lastNightTarifaId } = useMemo(() => {
        const fechaBaseRaw = UTCStringToLocalDate(habitacion?.ultima_renta?.fecha_condensada)
        const fechaBase = new Date(fechaBaseRaw.getFullYear(), fechaBaseRaw.getMonth(), fechaBaseRaw.getDate(), 0, 0, 0)
        const totalNights = hospedajesExtra || 0
        const nightAssignments: string[] = []

        for (let i = 0; i < totalNights; i++) {
            const nightDate = addDays({ date: fechaBase, days: i })
            let winnerId: string | null = null

            for (const { gp } of priorityGroups) {
                const isBase = gp.modalidad === ModalidadGrupoTarifa.Base
                const gpStart = setStartDate(gp.fecha_inicio)
                const gpEnd = setStartDate(gp.fecha_fin)

                if (isBase || (nightDate >= gpStart && nightDate <= gpEnd)) {
                    const tarifa = gp.tarifas?.[0]
                    if (tarifa) { winnerId = tarifa.tarifa_id; break }
                }
            }

            if (winnerId) nightAssignments.push(winnerId)
        }

        const nights: Record<string, number> = {}
        const dateRanges: Record<string, { start: Date, end: Date }> = {}

        nightAssignments.forEach((id, i) => {
            nights[id] = (nights[id] || 0) + 1
            const nightDate = addDays({ date: fechaBase, days: i })
            if (!dateRanges[id]) {
                dateRanges[id] = { start: nightDate, end: nightDate }
            } else {
                dateRanges[id].end = nightDate
            }
        })

        return {
            effectiveNights: nights,
            tarifaDateRanges: dateRanges,
            lastNightTarifaId: nightAssignments[nightAssignments.length - 1] ?? null
        }
    }, [priorityGroups, hospedajesExtra, habitacion])

    // Tarifa que absorbe el costo de horas extra (la que cubre la última noche, o la de mayor prioridad vigente si solo hay horas)
    const hoursTarifaId = useMemo(() => {
        if (lastNightTarifaId) return lastNightTarifaId
        if (!horasExtras) return null
        const fechaBaseRaw = UTCStringToLocalDate(habitacion?.ultima_renta?.fecha_condensada)
        const fechaBase = new Date(fechaBaseRaw.getFullYear(), fechaBaseRaw.getMonth(), fechaBaseRaw.getDate(), 0, 0, 0)
        for (const { gp } of priorityGroups) {
            const isBase = gp.modalidad === ModalidadGrupoTarifa.Base
            const gpStart = setStartDate(gp.fecha_inicio)
            const gpEnd = setStartDate(gp.fecha_fin)
            if (isBase || (fechaBase >= gpStart && fechaBase <= gpEnd)) {
                const tarifa = gp.tarifas?.[0]
                if (tarifa) return tarifa.tarifa_id
            }
        }
        return null
    }, [lastNightTarifaId, horasExtras, priorityGroups, habitacion])

    useEffect(() => {
        const allTarifas = [
            ...gruposTarifasAdicionalesSelected.flatMap(gp => gp.tarifas || []),
            ...gruposTarifasSelected.flatMap(gp => gp.tarifas || []),
        ]

        const costoHospedajes = sum(allTarifas.map(t =>
            times(t.costo_hospedaje_extra || 0, isMotel ? (hospedajesExtra || 0) : (effectiveNights[t.tarifa_id] || 0))
        ))
        const costoHoras = horasExtras
            ? sum(allTarifas
                .filter(t => t.tarifa_id === hoursTarifaId)
                .map(t => times(t.costo_hora_extra || 0, horasExtras)))
            : 0
        const costoPersonas = hospedajesExtra
            ? sum(allTarifas.map(t =>
                times(times(t.costo_persona_extra || 0, effectiveNights[t.tarifa_id] || 0), personasExtras || 0)
            ))
            : horasExtras
                ? sum(allTarifas
                    .filter(t => t.tarifa_id === hoursTarifaId)
                    .map(t => times(t.costo_persona_extra || 0, personasExtras || 0)))
                : 0

        const totalBase = sum([costoHospedajes, costoHoras, costoPersonas])

        setCosts(v => ({
            ...v,
            baseTotal: totalBase,
            total: sum([totalBase, -descuento.total || 0]),
            totalConIva: sum([totalBase, -(descuento.total || 0)]),
            subtotal: getSubtotal(totalBase),
            hospedajes: getSubtotal(costoHospedajes),
            extraHours: getSubtotal(costoHoras),
            extraUsers: getSubtotal(costoPersonas),
            tax: getIVA(totalBase),
            totalConPropinas: sum([totalBase, -descuento.total || 0, totalPropinas])
        }))
    }, [gruposTarifasSelected, gruposTarifasAdicionalesSelected, totalPropinas, descuento.total, effectiveNights, hoursTarifaId, horasExtras, personasExtras, hospedajesExtra, isMotel])

    const rangoStartDate = UTCStringToLocalDate(habitacion?.ultima_renta?.fecha_condensada)

    const getRangoEstancia = () => {
        const fecha_inicio = rangoStartDate
        if (habitacion?.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Hotel) {
            const fecha_fin = addDays({ date: fecha_inicio, days: getValues("principal.nochesExtraCount") || 0 })
            if (diffDays(fecha_inicio, fecha_fin) > 0) {
                return `${formatCustomDate(fecha_inicio, "MMM, DD")} - ${formatCustomDate(fecha_fin, "MMM, DD")}`
            }
            return formatCustomDate(fecha_inicio, "MMM, DD")
        }
        const fecha_fin = addExtraHours(fecha_inicio, times((getValues("principal.nochesExtraCount") || 0), tarifaBaseHabitacion?.duracion_renta || 0))
        if (diffDays(fecha_inicio, fecha_fin) > 0 && !areSameDay(fecha_inicio, fecha_fin)) {
            return `${formatCustomDate(fecha_inicio, "MMM, DD")} - ${formatCustomDate(fecha_fin, "MMM, DD")}`
        }
        return formatCustomDate(fecha_inicio, "MMM, DD")
    }

    return (
        <Resumen>
            <ResumenContent>
                <ResumenSection>
                    <ResumenTitle>Resumen</ResumenTitle>
                    <Detail color='var(--tipografa)'>
                        <DetailIcon name='calendarFill' />
                        <DetailContent>
                            <DetailRow>
                                <DetailText type='normal'>
                                    Estancia
                                </DetailText>
                            </DetailRow>
                            <DetailRow>
                                <DetailText type='bold'>
                                    {getRangoEstancia()}
                                </DetailText>
                            </DetailRow>
                        </DetailContent>
                    </Detail>
                    <Detail color='var(--tipografa)'>
                        <DetailIcon name='userFilled' />
                        <DetailContent>
                            <DetailRow>
                                <DetailText type='normal'>
                                    Nombre completo del huésped
                                </DetailText>
                            </DetailRow>
                            <DetailRow>
                                <DetailText type='bold'>
                                    {habitacion?.ultima_renta?.nombre_huesped || "-"}
                                </DetailText>
                            </DetailRow>
                        </DetailContent>
                    </Detail>
                    <Detail color='var(--tipografa)'>
                        <DetailIcon name='dollarCircle' />
                        <DetailContent>
                            <DetailRow>
                                <DetailText type='normal'>
                                    Tarifas
                                </DetailText>
                            </DetailRow>
                            {
                                [...gruposTarifasAdicionalesSelected, ...gruposTarifasSelected].map((gp, index) =>
                                    gp.tarifas?.map((tarifa, tindex) => {
                                        const nights = effectiveNights[tarifa.tarifa_id] || 0
                                        const isHoursTarifa = tarifa.tarifa_id === hoursTarifaId
                                        const isHotel = habitacion?.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Hotel
                                        const costoHospedajesEfectivo = times(tarifa.costo_hospedaje_extra || 0, (isHotel ? nights : (hospedajesExtra || 0)))
                                        const costoHorasEfectivo = isHoursTarifa ? times(tarifa.costo_hora_extra || 0, horasExtras || 0) : 0
                                        if (nights === 0 && !isHoursTarifa) return null
                                        const range = tarifaDateRanges[tarifa.tarifa_id]
                                        const rangeText = range
                                            ? diffDays(range.start, range.end) > 0
                                                ? `${formatDateShort(range.start)} - ${formatDateShort(range.end)}`
                                                : formatDateShort(range.start)
                                            : ''
                                        return (
                                            <Fragment key={`${index} ${tindex}`}>
                                                <DetailRow>
                                                    <DetailText type='bold' style={{ maxWidth: "250px" }}>
                                                        {gp.modalidad === ModalidadGrupoTarifa.Base
                                                            ? capitalize(gp.modalidad)
                                                            : `${capitalize(gp.modalidad)} - ${tarifa?.grupo_tarifa?.nombre || "-"}`}
                                                    </DetailText>
                                                </DetailRow>
                                                {nights > 0 &&
                                                    <DetailRow>
                                                        <DetailText style={{ maxWidth: "200px" }} type='normal'>
                                                            {isHotel
                                                                ? `${rangeText} | ${formatCurrencyToFixed(tarifa.costo_hospedaje_extra)}`
                                                                : `1 hospedaje extra | ${formatCurrencyToFixed(tarifa.costo_hospedaje_extra)}`}
                                                        </DetailText>
                                                        <DetailText style={{ maxWidth: "75px" }} textAlign='right' type="normal">
                                                            {formatCurrencyToFixed(costoHospedajesEfectivo)}
                                                        </DetailText>
                                                    </DetailRow>
                                                }
                                                {costoHorasEfectivo > 0 &&
                                                    <DetailRow>
                                                        <DetailText style={{ maxWidth: "200px" }} type='normal'>
                                                            {isHotel
                                                                ? `${horasExtras || 0} horas extra | ${formatCurrencyToFixed(tarifa.costo_hora_extra)}`
                                                                : `1 hora extra | ${formatCurrencyToFixed(tarifa.costo_hora_extra)}`}
                                                        </DetailText>
                                                        <DetailText style={{ maxWidth: "75px" }} textAlign='right' type="normal">
                                                            {formatCurrencyToFixed(costoHorasEfectivo)}
                                                        </DetailText>
                                                    </DetailRow>
                                                }
                                            </Fragment>
                                        )
                                    })
                                )
                            }
                        </DetailContent>
                    </Detail>
                    {costs.extraUsers > 0 &&
                        <Detail color='var(--tipografa)'>
                            <DetailIcon name='UserParentFill' />
                            <DetailContent>
                                <DetailRow>
                                    <DetailText type='normal'>
                                        Personas extra
                                    </DetailText>
                                </DetailRow>
                                {[...gruposTarifasAdicionalesSelected, ...gruposTarifasSelected].map((gp, index) =>
                                    gp.tarifas?.map((tarifa, tindex) => {
                                        let costoPersonasEfectivo = 0
                                        if (hospedajesExtra) {
                                            const nights = effectiveNights[tarifa.tarifa_id] || 0
                                            if (nights === 0) return null
                                            costoPersonasEfectivo = times(times(tarifa.costo_persona_extra || 0, nights), personasExtras || 0)
                                        } else if (horasExtras) {
                                            if (tarifa.tarifa_id !== hoursTarifaId) return null
                                            costoPersonasEfectivo = times(tarifa.costo_persona_extra || 0, personasExtras || 0)
                                        } else {
                                            return null
                                        }
                                        if (costoPersonasEfectivo === 0) return null
                                        return (
                                            <Fragment key={`${index} ${tindex}`}>
                                                <DetailRow>
                                                    <DetailText type='bold' style={{ maxWidth: "250px" }}>
                                                        {gp.modalidad === ModalidadGrupoTarifa.Base
                                                            ? capitalize(gp.modalidad)
                                                            : `${capitalize(gp.modalidad)} - ${tarifa?.grupo_tarifa?.nombre || "-"}`}
                                                    </DetailText>
                                                </DetailRow>
                                                <DetailRow>
                                                    <DetailText style={{ maxWidth: "200px" }} type='normal'>
                                                        {habitacion?.ultima_renta?.tipo_alojamiento === TiposAlojamientos.Hotel
                                                            ? `1 persona - ${formatCurrencyToFixed(tarifa.costo_persona_extra)}`
                                                            : `1 persona extra | ${formatCurrencyToFixed(tarifa.costo_persona_extra)}`}
                                                    </DetailText>
                                                    <DetailText style={{ maxWidth: "75px" }} textAlign='right' type="normal">
                                                        {formatCurrencyToFixed(costoPersonasEfectivo)}
                                                    </DetailText>
                                                </DetailRow>
                                            </Fragment>
                                        )
                                    })
                                )}
                            </DetailContent>
                        </Detail>
                    }
                    {
                        tipoPago === ModalidadDePago.Pendiente ?
                            <Detail color='var(--tipografa)'>
                                <DetailIcon name='BankCard' />
                                <DetailContent>
                                    <DetailRow>
                                        <DetailText type='normal'>
                                            Método de pago
                                        </DetailText>
                                    </DetailRow>
                                    <Fragment>
                                        <DetailRow>
                                            <DetailText type='bold'>
                                                -
                                            </DetailText>
                                        </DetailRow>
                                    </Fragment>
                                </DetailContent>
                            </Detail> : !!pagos?.every(p => !!p?.method) &&
                            <Detail color='var(--tipografa)'>
                                <DetailIcon name='BankCard' />
                                <DetailContent>
                                    <DetailRow>
                                        <DetailText type='normal'>
                                            Método de pago
                                        </DetailText>
                                    </DetailRow>
                                    {pagos.map((pago, index) =>
                                        <Fragment key={index}>
                                            <DetailRow>
                                                <DetailText type='bold'>
                                                    {formatTipoPago(pago.method)}
                                                </DetailText>
                                                <DetailText textAlign='right' type="bold">
                                                    {formatCurrencyToFixed(pago.amount)}
                                                </DetailText>
                                            </DetailRow>
                                        </Fragment>
                                    )}
                                </DetailContent>
                            </Detail>
                    }
                </ResumenSection>
                <ResumenSection>
                    <DetailDivider color='var(--placeholder)' />
                    <Detail color='var(--tipografa)'>
                        <DetailContent>
                            {
                                !!hospedajesExtra &&
                                <DetailRow>
                                    <DetailText type='bold'>
                                        Estancia ({hospedajesExtra} noches)
                                    </DetailText>
                                    <DetailText textAlign='right' textWeight={500}>
                                        {formatCurrencyToFixed(costs.hospedajes)}
                                    </DetailText>
                                </DetailRow>
                            }
                            {
                                !!horasExtras &&
                                <DetailRow>
                                    <DetailText type='bold'>
                                        Hora extra ({horasExtras})
                                    </DetailText>
                                    <DetailText textAlign='right' textWeight={500} type='normal'>
                                        {formatCurrencyToFixed(costs.extraHours)}
                                    </DetailText>
                                </DetailRow>
                            }
                            {
                                !!personasExtras &&
                                <DetailRow>
                                    <DetailText type='bold'>
                                        Personas extra ({personasExtras})
                                    </DetailText>
                                    <DetailText textAlign='right' textWeight={500} type='normal'>
                                        {formatCurrencyToFixed(costs.extraUsers)}
                                    </DetailText>
                                </DetailRow>
                            }
                            {
                                sum(pagos?.map(p => p.propina?.monto || Number(p.propina?.porcentaje || 0) || 0) || []) > 0 &&
                                <DetailRow>
                                    <DetailText type='bold'>
                                        Propinas
                                    </DetailText>
                                    <DetailText textAlign='right' type='normal'>
                                        {formatCurrencyToFixed(totalPropinas)}
                                    </DetailText>
                                </DetailRow>
                            }
                            <DetailRow>
                                <DetailText type='bold' customColor={"var(--placeholder)"}>
                                    Impuestos
                                </DetailText>
                                <DetailText textAlign='right' type='normal' customColor={"var(--placeholder)"}>
                                    {formatCurrencyToFixed(costs.tax)}
                                </DetailText>
                            </DetailRow>
                            <DetailRow style={{ marginBottom: "10px" }}>
                                <DetailText type='bold' style={{ maxWidth: "200px" }} >
                                    {descuento.nombre
                                        ? `${descuento.nombre} ${descuento.tipo_descuento === 'porcentaje' ? `-${descuento.valor}%` : `-$${descuento.valor}`}`
                                        : "Descuento"}
                                </DetailText>
                                <DetailAction disabled={descuentoDesactivado || (!hospedajesExtra && !horasExtras && !personasExtras)} color={"var(--primary)"} onClick={onOpenDescuento}>
                                    {descuento.total > 0 ? `-${getCurrencyFormat(descuento.total)}` : "Agregar"}
                                </DetailAction>
                            </DetailRow>
                            <DetailRow>
                                <DetailText type='bold' textSize={20} textWeight={700}>
                                    Total
                                </DetailText>
                                <DetailText textAlign='right' type='bold' textSize={20} textWeight={700}>
                                    {formatCurrencyToFixed(costs.totalConPropinas)}
                                </DetailText>
                            </DetailRow>
                        </DetailContent>
                    </Detail>
                </ResumenSection>
            </ResumenContent>
            <ResumenFooter buttonForFormID={formId} buttonText='Renovar estancia' />
        </Resumen>
    )
}

export default RenovacionResumen